// frontend/src/pages/UserNotifications.tsx
// Full notification list view

import React, { useEffect, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import {
  Notification,
  getNotificationSeverity,
  getUserNotifications,
  markNotificationAsRead
} from '../services/notificationApi';
import { useSidebar } from '../contexts/SidebarContext';
import { useNewGui } from '../hooks/useNewGui';
import PageHeader from '../components/shared/PageHeader';

const UserNotifications: React.FC = () => {
  const { isSidebarOpen } = useSidebar();
  const useNew = useNewGui();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);

  const loadNotifications = async () => {
    setLoading(true);
    try {
      const response = await getUserNotifications();
      if (response.success) {
        setNotifications(response.data);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNotifications();
  }, []);

  const handleMarkRead = async (notificationId: number) => {
    await markNotificationAsRead(notificationId);
    setNotifications(prev =>
      prev.map(n => (n.id === notificationId ? { ...n, is_read: true } : n))
    );
  };

  const renderPriority = (rowData: Notification) => {
    const severity = getNotificationSeverity(rowData.priority);
    const label = rowData.priority === 'CRITICAL' ? 'Critical' : rowData.priority;
    return <Tag severity={severity as any} value={label} />;
  };

  const renderDate = (rowData: Notification) => {
    return new Date(rowData.created_at).toLocaleString();
  };

  const renderActions = (rowData: Notification) => {
    const actionUrl = rowData.action_url
      ? rowData.action_url.startsWith('/')
        ? `${window.location.origin}/#${rowData.action_url}`
        : rowData.action_url
      : null;

    return (
      <div className="flex gap-2">
        {!rowData.is_read && (
          <Button
            icon="pi pi-check"
            size="small"
            text
            onClick={() => handleMarkRead(rowData.id)}
            tooltip="Mark as read"
            tooltipOptions={{ position: 'top' }}
          />
        )}
        {actionUrl && (
          <Button
            icon="pi pi-external-link"
            size="small"
            text
            onClick={() => window.open(actionUrl, '_blank', 'noopener,noreferrer')}
            tooltip="Open"
            tooltipOptions={{ position: 'top' }}
          />
        )}
      </div>
    );
  };

  return (
    <div className={`p-6 transition-all duration-300 ${isSidebarOpen ? 'ml-64' : 'ml-20'} ${useNew ? 'font-plex' : ''}`}>
      {useNew ? (
        <PageHeader
          title="Notifications"
          purpose="Your account notifications."
          actions={<Button label="Refresh" icon="pi pi-refresh" size="small" onClick={loadNotifications} />}
        />
      ) : (
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-semibold">Notifications</h2>
          <Button label="Refresh" icon="pi pi-refresh" size="small" onClick={loadNotifications} />
        </div>
      )}

      <div className={useNew ? "bg-surface border border-border-subtle rounded-lg shadow-sm p-4" : "bg-white rounded-lg shadow-sm p-4"}>
        <DataTable
          value={notifications}
          loading={loading}
          paginator
          rows={10}
          rowsPerPageOptions={[10, 20, 50]}
          emptyMessage="No notifications found."
          className="p-datatable-sm"
          stripedRows
          dataKey="id"
        >
          <Column field="title" header="Title" sortable style={{ minWidth: '180px' }} />
          <Column field="message" header="Message" style={{ minWidth: '280px' }} />
          <Column field="notification_type" header="Type" sortable style={{ minWidth: '180px' }} />
          <Column header="Priority" body={renderPriority} style={{ minWidth: '120px' }} />
          <Column header="Created" body={renderDate} sortable style={{ minWidth: '160px' }} />
          <Column header="Read" body={(rowData: Notification) => (rowData.is_read ? 'Yes' : 'No')} style={{ minWidth: '80px' }} />
          <Column header="Actions" body={renderActions} style={{ minWidth: '120px' }} />
        </DataTable>
      </div>
    </div>
  );
};

export default UserNotifications;
